<template>
  <div class="energy-dashboard">
    <!-- 顶部标题栏 -->
    <header class="dashboard-header">
      <h1 class="main-title">节能管理监控中心</h1>
      <div class="header-info">
        <div class="time-date">{{ currentDateTime }}</div>
        <div class="system-status">
          <span class="status-indicator online"></span>
          <span>系统运行正常</span>
        </div>
      </div>
    </header>

    <!-- 主内容区域 -->
    <main class="dashboard-content">
      <!-- 左侧区域 -->
      <section class="left-panel">
        <!-- 总能耗统计 -->
        <div class="card energy-overview">
          <h2 class="card-title">总能耗统计</h2>
          <div class="overview-stats">
            <div class="stat-item">
              <div class="stat-value">{{ totalElectricity }}</div>
              <div class="stat-label">总用电量 (kWh)</div>
              <div class="stat-trend" :class="electricityTrend > 0 ? 'up' : 'down'">
                <i class="fa" :class="electricityTrend > 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
                {{ Math.abs(electricityTrend) }}%
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ totalWater }}</div>
              <div class="stat-label">总用水量 (m³)</div>
              <div class="stat-trend" :class="waterTrend > 0 ? 'up' : 'down'">
                <i class="fa" :class="waterTrend > 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
                {{ Math.abs(waterTrend) }}%
              </div>
            </div>
            <div class="stat-item">
              <div class="stat-value">{{ totalHeat }}</div>
              <div class="stat-label">总用热量 (GJ)</div>
              <div class="stat-trend" :class="heatTrend > 0 ? 'up' : 'down'">
                <i class="fa" :class="heatTrend > 0 ? 'fa-arrow-up' : 'fa-arrow-down'"></i>
                {{ Math.abs(heatTrend) }}%
              </div>
            </div>
          </div>
          <div class="energy-chart">
            <canvas id="energyConsumptionChart"></canvas>
          </div>
        </div>

        <!-- 节能效益 -->
        <div class="card energy-saving">
          <h2 class="card-title">节能效益分析</h2>
          <div class="saving-stats">
            <div class="saving-item">
              <div class="saving-icon">
                <i class="fa fa-bolt"></i>
              </div>
              <div class="saving-details">
                <div class="saving-value">{{ electricitySaved }}</div>
                <div class="saving-label">节约电量 (kWh)</div>
              </div>
            </div>
            <div class="saving-item">
              <div class="saving-icon">
                <i class="fa fa-tint"></i>
              </div>
              <div class="saving-details">
                <div class="saving-value">{{ waterSaved }}</div>
                <div class="saving-label">节约水量 (m³)</div>
              </div>
            </div>
            <div class="saving-item">
              <div class="saving-icon">
                <i class="fa fa-fire"></i>
              </div>
              <div class="saving-details">
                <div class="saving-value">{{ costSaved }}</div>
                <div class="saving-label">节约成本 (元)</div>
              </div>
            </div>
          </div>
          <div class="saving-chart">
            <canvas id="savingTrendChart"></canvas>
          </div>
        </div>
      </section>

      <!-- 中间区域 -->
      <section class="center-panel">
        <!-- 能源分布地图 -->
        <div class="card energy-map">
          <h2 class="card-title">能源消耗分布</h2>
          <div class="map-container">
<!--            <img src="https://picsum.photos/800/500" alt="能源消耗分布地图" class="map-image">-->
            <div class="map-markers">
              <div class="marker high" style="top: 30%; left: 40%;" :title="`区域A: ${markers[0].value} kWh`"></div>
              <div class="marker medium" style="top: 45%; left: 60%;" :title="`区域B: ${markers[1].value} kWh`"></div>
              <div class="marker low" style="top: 60%; left: 30%;" :title="`区域C: ${markers[2].value} kWh`"></div>
              <div class="marker high" style="top: 25%; left: 70%;" :title="`区域D: ${markers[3].value} kWh`"></div>
              <div class="marker medium" style="top: 55%; left: 50%;" :title="`区域E: ${markers[4].value} kWh`"></div>
            </div>
          </div>
          <div class="map-legend">
            <div class="legend-item">
              <span class="legend-color high"></span>
              <span>高消耗</span>
            </div>
            <div class="legend-item">
              <span class="legend-color medium"></span>
              <span>中消耗</span>
            </div>
            <div class="legend-item">
              <span class="legend-color low"></span>
              <span>低消耗</span>
            </div>
          </div>
        </div>

        <!-- 设备运行状态 -->
        <div class="card equipment-status">
          <h2 class="card-title">设备运行状态</h2>
          <div class="equipment-table">
            <table>
              <thead>
              <tr>
                <th>设备名称</th>
                <th>类型</th>
                <th>状态</th>
                <th>能耗</th>
                <th>运行时长</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(equip, index) in equipmentList" :key="index">
                <td>{{ equip.name }}</td>
                <td>{{ equip.type }}</td>
                <td>
                  <span class="status-badge" :class="equip.status">{{ equip.statusText }}</span>
                </td>
                <td>{{ equip.energyConsumption }}</td>
                <td>{{ equip.runTime }}</td>
              </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <!-- 右侧区域 -->
      <section class="right-panel">
        <!-- 实时监控数据 -->
        <div class="card real-time-monitor">
          <h2 class="card-title">实时监控数据</h2>
          <div class="monitor-items">
            <div class="monitor-item">
              <div class="monitor-label">电压</div>
              <div class="monitor-value">{{ voltage }} V</div>
              <div class="monitor-status normal">正常</div>
            </div>
            <div class="monitor-item">
              <div class="monitor-label">电流</div>
              <div class="monitor-value">{{ current }} A</div>
              <div class="monitor-status normal">正常</div>
            </div>
            <div class="monitor-item">
              <div class="monitor-label">功率</div>
              <div class="monitor-value">{{ power }} kW</div>
              <div class="monitor-status attention">偏高</div>
            </div>
            <div class="monitor-item">
              <div class="monitor-label">水温</div>
              <div class="monitor-value">{{ waterTemp }} °C</div>
              <div class="monitor-status normal">正常</div>
            </div>
            <div class="monitor-item">
              <div class="monitor-label">流量</div>
              <div class="monitor-value">{{ waterFlow }} m³/h</div>
              <div class="monitor-status normal">正常</div>
            </div>
            <div class="monitor-item">
              <div class="monitor-label">压力</div>
              <div class="monitor-value">{{ pressure }} MPa</div>
              <div class="monitor-status normal">正常</div>
            </div>
          </div>
        </div>

        <!-- 告警信息 -->
        <div class="card alarm-info">
          <h2 class="card-title">告警信息</h2>
          <div class="alarm-list">
            <div class="alarm-item" v-for="(alarm, index) in alarmList" :key="index">
              <div class="alarm-icon" :class="alarm.level"></div>
              <div class="alarm-details">
                <div class="alarm-title">{{ alarm.title }}</div>
                <div class="alarm-time">{{ alarm.time }}</div>
              </div>
              <div class="alarm-handle">
                <button class="handle-btn">处理</button>
              </div>
            </div>
          </div>
        </div>

        <!-- 能源效率排名 -->
        <div class="card efficiency-ranking">
          <h2 class="card-title">区域能源效率排名</h2>
          <div class="ranking-list">
            <div class="ranking-item" v-for="(item, index) in efficiencyRanking" :key="index">
              <div class="ranking-position">{{ index + 1 }}</div>
              <div class="ranking-name">{{ item.area }}</div>
              <div class="ranking-score">{{ item.score }}</div>
              <div class="ranking-bar">
                <div class="bar-fill" :style="{ width: `${item.score}%` }"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { onMounted, ref, watchEffect } from 'vue';
import * as Chart from 'echarts';

export default {
  name: 'EnergyDashboard',
  setup() {
    // 时间日期数据
    const currentDateTime = ref('');

    // 更新时间日期
    const updateDateTime = () => {
      const now = new Date();
      currentDateTime.value = now.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).replace(/\//g, '-');
    };

    // 初始化时间并设置定时器
    updateDateTime();
    setInterval(updateDateTime, 1000);

    // 总能耗数据
    const totalElectricity = ref('128,456');
    const totalWater = ref('8,732');
    const totalHeat = ref('4,219');

    // 能耗趋势
    const electricityTrend = ref(-3.2);
    const waterTrend = ref(-1.8);
    const heatTrend = ref(0.5);

    // 节能数据
    const electricitySaved = ref('5,632');
    const waterSaved = ref('328');
    const costSaved = ref('28,756');

    // 地图标记数据
    const markers = ref([
      { value: 12500, level: 'high' },
      { value: 7800, level: 'medium' },
      { value: 3200, level: 'low' },
      { value: 14200, level: 'high' },
      { value: 6500, level: 'medium' }
    ]);

    // 设备列表数据
    const equipmentList = ref([
      { name: '中央空调', type: '空调系统', status: 'normal', statusText: '运行中', energyConsumption: '12.5 kW', runTime: '128h' },
      { name: '循环水泵', type: '给排水', status: 'normal', statusText: '运行中', energyConsumption: '3.2 kW', runTime: '240h' },
      { name: '照明系统', type: '电气', status: 'normal', statusText: '运行中', energyConsumption: '5.8 kW', runTime: '86h' },
      { name: '电梯#1', type: '特种设备', status: 'warning', statusText: '异常', energyConsumption: '7.3 kW', runTime: '45h' },
      { name: '冷却塔', type: '空调系统', status: 'normal', statusText: '运行中', energyConsumption: '2.1 kW', runTime: '98h' }
    ]);

    // 实时监控数据
    const voltage = ref('380.5');
    const current = ref('24.3');
    const power = ref('9.2');
    const waterTemp = ref('45.2');
    const waterFlow = ref('12.8');
    const pressure = ref('0.45');

    // 模拟实时数据变化
    setInterval(() => {
      voltage.value = (380 + Math.random() * 2 - 1).toFixed(1);
      current.value = (24 + Math.random() * 2 - 1).toFixed(1);
      power.value = (9 + Math.random() * 1 - 0.3).toFixed(1);
      waterTemp.value = (45 + Math.random() * 2 - 1).toFixed(1);
      waterFlow.value = (12 + Math.random() * 2 - 1).toFixed(1);
      pressure.value = (0.45 + Math.random() * 0.1 - 0.05).toFixed(2);
    }, 5000);

    // 告警信息
    const alarmList = ref([
      { title: '电梯#1 电流异常升高', level: 'high', time: '10:23:45' },
      { title: '区域B 用水量超出阈值', level: 'medium', time: '09:15:32' },
      { title: '变压器温度偏高', level: 'medium', time: '08:47:19' },
      { title: '区域C 照明系统故障', level: 'low', time: '07:32:51' }
    ]);

    // 能源效率排名
    const efficiencyRanking = ref([
      { area: '区域C', score: 92 },
      { area: '区域E', score: 87 },
      { area: '区域B', score: 78 },
      { area: '区域D', score: 65 },
      { area: '区域A', score: 59 }
    ]);

    // 初始化图表
    onMounted(() => {
      // 能耗趋势图表
      const energyCtx = document.getElementById('energyConsumptionChart').getContext('2d');
      new Chart(energyCtx, {
        type: 'line',
        data: {
          labels: ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'],
          datasets: [
            {
              label: '电力',
              data: [12500, 13200, 11800, 10500, 9800, 10200, 14500, 15200, 12800, 11200, 10800, 12100],
              borderColor: '#41b6e6',
              backgroundColor: 'rgba(65, 182, 230, 0.1)',
              tension: 0.4,
              fill: true
            },
            {
              label: '水资源',
              data: [850, 820, 780, 720, 650, 680, 920, 980, 850, 790, 760, 810],
              borderColor: '#78c2ad',
              backgroundColor: 'rgba(120, 194, 173, 0.1)',
              tension: 0.4,
              fill: true
            },
            {
              label: '热能',
              data: [420, 450, 380, 250, 180, 150, 120, 130, 210, 320, 380, 410],
              borderColor: '#ff7c43',
              backgroundColor: 'rgba(255, 124, 67, 0.1)',
              tension: 0.4,
              fill: true
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'top',
              labels: {
                color: '#e0e0e0'
              }
            }
          },
          scales: {
            x: {
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
              ticks: {
                color: '#b0b0b0'
              }
            },
            y: {
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
              ticks: {
                color: '#b0b0b0'
              }
            }
          }
        }
      });

      // 节能趋势图表
      const savingCtx = document.getElementById('savingTrendChart').getContext('2d');
      new Chart(savingCtx, {
        type: 'bar',
        data: {
          labels: ['1月', '2月', '3月', '4月', '5月', '6月'],
          datasets: [
            {
              label: '节约成本 (元)',
              data: [3200, 4500, 3800, 5200, 6100, 4800],
              backgroundColor: [
                'rgba(75, 192, 192, 0.7)',
                'rgba(75, 192, 192, 0.7)',
                'rgba(75, 192, 192, 0.7)',
                'rgba(75, 192, 192, 0.7)',
                'rgba(75, 192, 192, 0.7)',
                'rgba(75, 192, 192, 0.7)'
              ],
              borderColor: [
                'rgba(75, 192, 192, 1)',
                'rgba(75, 192, 192, 1)',
                'rgba(75, 192, 192, 1)',
                'rgba(75, 192, 192, 1)',
                'rgba(75, 192, 192, 1)',
                'rgba(75, 192, 192, 1)'
              ],
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              display: false
            }
          },
          scales: {
            x: {
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
              ticks: {
                color: '#b0b0b0'
              }
            },
            y: {
              grid: {
                color: 'rgba(255, 255, 255, 0.1)'
              },
              ticks: {
                color: '#b0b0b0'
              }
            }
          }
        }
      });
    });

    return {
      currentDateTime,
      totalElectricity,
      totalWater,
      totalHeat,
      electricityTrend,
      waterTrend,
      heatTrend,
      electricitySaved,
      waterSaved,
      costSaved,
      markers,
      equipmentList,
      voltage,
      current,
      power,
      waterTemp,
      waterFlow,
      pressure,
      alarmList,
      efficiencyRanking
    };
  }
};
</script>

<style scoped>
.energy-dashboard {
  width: 100vw;
  height: 100vh;
  background-color: #0f1733;
  color: #ffffff;
  padding: 20px;
  box-sizing: border-box;
  overflow: hidden;
  background-image:
    radial-gradient(circle at 10% 20%, rgba(30, 60, 114, 0.1) 0%, transparent 20%),
    radial-gradient(circle at 90% 80%, rgba(30, 60, 114, 0.1) 0%, transparent 20%);
}

.dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.main-title {
  font-size: 28px;
  font-weight: 600;
  color: #41b6e6;
  margin: 0;
  text-shadow: 0 0 10px rgba(65, 182, 230, 0.3);
}

.header-info {
  display: flex;
  align-items: center;
  gap: 20px;
}

.time-date {
  color: #b0b0b0;
  font-size: 16px;
}

.system-status {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #e0e0e0;
}

.status-indicator {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}

.status-indicator.online {
  background-color: #4cd964;
  box-shadow: 0 0 10px rgba(76, 217, 100, 0.6);
}

.dashboard-content {
  display: flex;
  gap: 20px;
  height: calc(100% - 80px);
}

.left-panel, .center-panel, .right-panel {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.left-panel {
  width: 30%;
}

.center-panel {
  width: 40%;
}

.right-panel {
  width: 30%;
}

.card {
  background-color: rgba(20, 30, 60, 0.6);
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(5px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
}

.card-title {
  font-size: 18px;
  margin: 0 0 15px 0;
  color: #e0e0e0;
  display: flex;
  align-items: center;
}

.card-title::before {
  content: '';
  display: inline-block;
  width: 4px;
  height: 16px;
  background-color: #41b6e6;
  margin-right: 8px;
  border-radius: 2px;
}

/* 总能耗统计样式 */
.overview-stats {
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
}

.stat-item {
  text-align: center;
  flex: 1;
  padding: 0 10px;
}

.stat-value {
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 5px;
  color: #ffffff;
}

.stat-label {
  font-size: 14px;
  color: #b0b0b0;
  margin-bottom: 5px;
}

.stat-trend {
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.stat-trend.up {
  color: #ff4d4f;
}

.stat-trend.down {
  color: #52c41a;
}

.energy-chart {
  height: 200px;
  width: 100%;
}

/* 节能效益样式 */
.saving-stats {
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
}

.saving-item {
  display: flex;
  align-items: center;
  gap: 10px;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 10px;
  border-radius: 6px;
  flex: 1;
  margin: 0 5px;
}

.saving-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(65, 182, 230, 0.1);
  color: #41b6e6;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
}

.saving-details {
  flex: 1;
}

.saving-value {
  font-size: 18px;
  font-weight: 600;
  color: #ffffff;
}

.saving-label {
  font-size: 12px;
  color: #b0b0b0;
}

.saving-chart {
  height: 160px;
  width: 100%;
}

/* 能源分布地图样式 */
.energy-map {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.map-container {
  flex: 1;
  position: relative;
  border-radius: 6px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.03);
  margin-bottom: 15px;
}

.map-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  opacity: 0.6;
}

.map-markers {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.marker {
  position: absolute;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  cursor: pointer;
  animation: pulse 2s infinite;
}

.marker.high {
  background-color: #ff4d4f;
  box-shadow: 0 0 0 rgba(255, 77, 79, 0.4);
}

.marker.medium {
  background-color: #faad14;
  box-shadow: 0 0 0 rgba(250, 173, 20, 0.4);
}

.marker.low {
  background-color: #52c41a;
  box-shadow: 0 0 0 rgba(82, 196, 26, 0.4);
}

@keyframes pulse {
  0% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 77, 79, 0.7);
  }

  70% {
    transform: scale(1);
    box-shadow: 0 0 0 10px rgba(255, 77, 79, 0);
  }

  100% {
    transform: scale(0.95);
    box-shadow: 0 0 0 0 rgba(255, 77, 79, 0);
  }
}

.map-legend {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  color: #b0b0b0;
}

.legend-color {
  width: 12px;
  height: 12px;
  border-radius: 3px;
}

.legend-color.high {
  background-color: #ff4d4f;
}

.legend-color.medium {
  background-color: #faad14;
}

.legend-color.low {
  background-color: #52c41a;
}

/* 设备运行状态样式 */
.equipment-status {
  max-height: 300px;
}

.equipment-table {
  overflow-y: auto;
  max-height: 240px;
}

.equipment-table table {
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
}

.equipment-table th,
.equipment-table td {
  padding: 10px 8px;
  text-align: left;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.equipment-table th {
  color: #b0b0b0;
  font-weight: 500;
  position: sticky;
  top: 0;
  background-color: rgba(20, 30, 60, 0.8);
}

.equipment-table td {
  color: #e0e0e0;
}

.status-badge {
  padding: 3px 8px;
  border-radius: 12px;
  font-size: 12px;
}

.status-badge.normal {
  background-color: rgba(82, 196, 26, 0.2);
  color: #52c41a;
}

.status-badge.warning {
  background-color: rgba(250, 173, 20, 0.2);
  color: #faad14;
}

.status-badge.error {
  background-color: rgba(255, 77, 79, 0.2);
  color: #ff4d4f;
}

/* 实时监控数据样式 */
.real-time-monitor {
  max-height: 280px;
}

.monitor-items {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
}

.monitor-item {
  background-color: rgba(255, 255, 255, 0.05);
  padding: 12px;
  border-radius: 6px;
}

.monitor-label {
  font-size: 14px;
  color: #b0b0b0;
  margin-bottom: 5px;
}

.monitor-value {
  font-size: 20px;
  font-weight: 600;
  color: #ffffff;
  margin-bottom: 5px;
}

.monitor-status {
  font-size: 12px;
  padding: 2px 6px;
  border-radius: 4px;
  display: inline-block;
}

.monitor-status.normal {
  background-color: rgba(82, 196, 26, 0.2);
  color: #52c41a;
}

.monitor-status.attention {
  background-color: rgba(250, 173, 20, 0.2);
  color: #faad14;
}

/* 告警信息样式 */
.alarm-info {
  flex: 1;
}

.alarm-list {
  overflow-y: auto;
  max-height: calc(100% - 40px);
}

.alarm-item {
  display: flex;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.alarm-item:last-child {
  border-bottom: none;
}

.alarm-icon {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  margin-right: 10px;
}

.alarm-icon.high {
  background-color: #ff4d4f;
}

.alarm-icon.medium {
  background-color: #faad14;
}

.alarm-icon.low {
  background-color: #faad14;
}

.alarm-details {
  flex: 1;
}

.alarm-title {
  font-size: 14px;
  color: #e0e0e0;
  margin-bottom: 3px;
}

.alarm-time {
  font-size: 12px;
  color: #888888;
}

.handle-btn {
  background-color: rgba(65, 182, 230, 0.2);
  color: #41b6e6;
  border: none;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.handle-btn:hover {
  background-color: rgba(65, 182, 230, 0.3);
}

/* 能源效率排名样式 */
.efficiency-ranking {
  max-height: 260px;
}

.ranking-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ranking-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
}

.ranking-position {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  color: #e0e0e0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
}

.ranking-name {
  width: 80px;
  color: #e0e0e0;
  font-size: 14px;
}

.ranking-score {
  width: 40px;
  text-align: right;
  color: #41b6e6;
  font-weight: 600;
}

.ranking-bar {
  flex: 1;
  height: 8px;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  background-color: #41b6e6;
  border-radius: 4px;
  transition: width 1s ease-in-out;
}

/* 滚动条样式优化 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}
</style>



// WEBPACK FOOTER //
// src/components/Screen.vue